import { useEffect, useRef } from 'react'
import styled from 'styled-components'
import * as echarts from 'echarts'

const AXIS_TEXT_COLOR = '#344054'

export interface ProfitShareSensitivityPoint {
  essProfitRate: number
  designedAverageSellUnit: number
  essProfit: number
  customerSavings: number
  afterCustomerCharge: number
}

interface Props {
  data: ProfitShareSensitivityPoint[]
  currentEssProfitRate: number
  height?: string
}

const Wrapper = styled.div`
  display: flex;
  width: 100%;
  padding: 16px;
  flex-direction: column;
  gap: 14px;
  border-radius: 4px;
  border: 1px solid #e6e6e6;
  background: #ffffff;
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const Title = styled.div`
  color: #2f3c5e;
  font-family: SUIT;
  font-size: 13px;
  font-weight: 700;
`

const Description = styled.div`
  color: #667085;
  font-family: SUIT;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.45;
`

const ChartBox = styled.div<{ height?: string }>`
  width: 100%;
  height: ${(props) => props.height || '260px'};
`

const CurrentSummaryGrid = styled.div`
  display: grid;
  width: 100%;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 8px;
`

const CurrentSummaryCard = styled.div`
  display: flex;
  min-width: 0;
  padding: 10px 12px;
  flex-direction: column;
  gap: 4px;
  border-radius: 4px;
  border: 1px solid rgba(87, 201, 167, 0.35);
  background: rgba(87, 201, 167, 0.08);
`

const CurrentSummaryLabel = styled.div`
  color: #667085;
  font-family: SUIT;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.35;
`

const CurrentSummaryValue = styled.div<{ $accent?: boolean }>`
  color: ${(props) => (props.$accent ? '#147a5b' : '#2f3c5e')};
  font-family: SUIT;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.3;
`

const formatWon = (value: number) => {
  if (Math.abs(value) >= 100000000) {
    return `${(value / 100000000).toFixed(1)}억`
  }

  if (Math.abs(value) >= 10000) {
    return `${(value / 10000).toFixed(0)}만`
  }

  return value.toLocaleString()
}

const formatWonDetail = (value: number) =>
  `${Number(value.toFixed(0)).toLocaleString()} 원`

const SellPlanProfitShareSensitivityChart = ({
  data,
  currentEssProfitRate,
  height,
}: Props) => {
  const chartRef = useRef<echarts.ECharts | null>(null)
  const chartId = useRef(
    `sell-plan-profit-share-${Math.floor(Math.random() * 1000000)}`
  )

  const currentPoint =
    data.find((point) => point.essProfitRate === currentEssProfitRate) ?? null
  const currentProfitPool =
    (currentPoint?.essProfit ?? 0) + (currentPoint?.customerSavings ?? 0)

  useEffect(() => {
    const chartDom = document.getElementById(chartId.current)
    if (!chartDom || data.length === 0) return

    if (chartRef.current) {
      chartRef.current.dispose()
    }

    const chart = echarts.init(chartDom)
    const option: echarts.EChartsOption = {
      color: ['#168C86', '#4F46E5'],
      legend: {
        data: ['ESS 사업자 이익', '수용가 절감액'],
        top: 0,
        left: 'center',
        textStyle: {
          fontSize: 11,
          color: '#667085',
          fontFamily: 'SUIT',
        },
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
        },
        formatter: (params: any) => {
          const hoveredRate = (
            params[0]?.data as [number, number] | undefined
          )?.[0]
          const profitLines = params.map((param: any) => {
            const point = param.data as [number, number] | undefined

            return `${param.marker}${param.seriesName}: <b>${formatWonDetail(
              point?.[1] ?? 0
            )}</b>`
          })

          return [
            `ESS 이익 비율: <b>${hoveredRate ?? '-'}%</b>`,
            ...profitLines,
          ].join('<br/>')
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '12%',
        top: 52,
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        min: 0,
        max: 100,
        name: 'ESS 사업자 이익 비율(%)',
        nameLocation: 'middle',
        nameGap: 28,
        nameTextStyle: {
          color: AXIS_TEXT_COLOR,
          fontFamily: 'SUIT',
          fontWeight: 400,
        },
        axisLabel: {
          color: AXIS_TEXT_COLOR,
          fontFamily: 'SUIT',
          fontWeight: 400,
        },
      },
      yAxis: {
        type: 'value',
        name: '이익 및 절감액(원)',
        nameTextStyle: {
          color: AXIS_TEXT_COLOR,
          fontFamily: 'SUIT',
          fontWeight: 400,
        },
        axisLabel: {
          color: AXIS_TEXT_COLOR,
          fontFamily: 'SUIT',
          fontWeight: 400,
          formatter: (value: number) => formatWon(value),
        },
        splitLine: {
          lineStyle: {
            color: '#e6e6e6',
            type: 'dashed',
          },
        },
      },
      series: [
        {
          name: 'ESS 사업자 이익',
          type: 'line',
          smooth: true,
          showSymbol: false,
          data: data.map((point) => [point.essProfitRate, point.essProfit]),
          lineStyle: {
            width: 3,
          },
          markLine: {
            symbol: ['none', 'none'],
            label: {
              formatter: '현재 이익 비율',
              color: '#4d4d4d',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              borderColor: '#e6e6e6',
              borderRadius: 4,
              borderWidth: 1,
              padding: [3, 6],
            },
            lineStyle: {
              color: '#4F46E5',
              type: 'dashed',
              width: 1.5,
            },
            data: [{ xAxis: currentEssProfitRate }],
          },
          animation: false,
        },
        {
          name: '수용가 절감액',
          type: 'line',
          smooth: true,
          showSymbol: false,
          data: data.map((point) => [
            point.essProfitRate,
            point.customerSavings,
          ]),
          lineStyle: {
            width: 2.5,
          },
          animation: false,
        },
      ],
    }

    const handleResize = () => {
      chart.resize()
    }

    chart.setOption(option)
    chartRef.current = chart
    window.addEventListener('resize', handleResize)

    return () => {
      chart.dispose()
      window.removeEventListener('resize', handleResize)
    }
  }, [currentEssProfitRate, data])

  return (
    <Wrapper>
      <Header>
        <Title>ESS 사업자 이익 비율 분석</Title>
        <Description>
          현재 설계 조건에서 ESS 사업자 이익 비율을 바꿨을 때 사업자 이익과
          수용가 절감액의 변화를 보여줍니다.
        </Description>
      </Header>
      <CurrentSummaryGrid>
        <CurrentSummaryCard>
          <CurrentSummaryLabel>현재 ESS 사업자 이익 비율</CurrentSummaryLabel>
          <CurrentSummaryValue $accent>
            ESS 사업자 {currentEssProfitRate}%
          </CurrentSummaryValue>
        </CurrentSummaryCard>
        <CurrentSummaryCard>
          <CurrentSummaryLabel>요금 설계 이익</CurrentSummaryLabel>
          <CurrentSummaryValue>
            {formatWonDetail(currentProfitPool)}
          </CurrentSummaryValue>
        </CurrentSummaryCard>
        <CurrentSummaryCard>
          <CurrentSummaryLabel>ESS 사업자 이익</CurrentSummaryLabel>
          <CurrentSummaryValue>
            {formatWonDetail(currentPoint?.essProfit ?? 0)}
          </CurrentSummaryValue>
        </CurrentSummaryCard>
        <CurrentSummaryCard>
          <CurrentSummaryLabel>수용가 절감액</CurrentSummaryLabel>
          <CurrentSummaryValue>
            {formatWonDetail(currentPoint?.customerSavings ?? 0)}
          </CurrentSummaryValue>
        </CurrentSummaryCard>
      </CurrentSummaryGrid>
      <ChartBox id={chartId.current} height={height} />
    </Wrapper>
  )
}

export default SellPlanProfitShareSensitivityChart
